import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { match, useHistory, useLocation, useParams } from 'react-router-dom';
import { generateUUID, renderTemplate } from '../../components/utils';
import { useConfig } from '../../config';
import { CmsIndex, hasCmsFile } from '../../utils/cmsIndex';
import { previewedFile } from '../../utils/draftPreview';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useTranslation } from '../../i18n/i18n';
import { fetchResourceMap, isNotFound, optionalResource, Res, useApi, useCms } from '../../state/api';
import {
	FILTER_RANGE_VALUE_DELIMITER,
	FilterContainer,
	isRangeFacetApplied as isRangeFacetAppliedStatic,
	resolveRangeSelectionForFacet,
} from '../../state/filters';
import { MetaContainer } from '../../state/meta';
import { UiContainer } from '../../state/ui';
import {
	ApiCategoryProductsLoop54,
	ApiFacetLoop54,
	ApiProduct,
	FacetPayloadLoop54,
	Loop54CategoryProductsRequestBody,
} from '../../types';
import { BrandBreadcrumbs } from '../Breadcrumbs/Breadcrumbs';
import { CmsComponent, JsxArgs } from '../cms/CmsComponent';
import { CmsPopularCategories, PopularCategoryItem } from '../cms/CmsPopularCategories/CmsPopularCategories';
import { FilterPanel } from '../FilterPanel/FilterPanel';
import { Main } from '../Main/Main';
import { FilterBadges } from '../Pagination/FilterBadges/FilterBadges';
import { FilterButton } from '../Pagination/FilterButton/FilterButton';
import { LOOP54_DEFAULT_PAGE_SIZE, PagesLoop54 } from '../Pagination/PaginationLoop54';
import { SortingChooser } from '../Pagination/SortingChooser/SortingChooser';
import { ProductBox } from '../ProductListing/ProductBox/ProductBox';
import { RawHtml } from '../RawHtml';
import { parsePagesQueryLoop54, Routing, useFilterQueryParams, useQuery } from '../Routes';
import './BrandPage.css';
import './CategoryPage.css';
import { determineSortPayload, multivalueFiltersToStrArray, rangeValueToFacetObj } from './CategoryPageLoop54';
import { LoaderPage } from './LoaderPage';
import { NotFoundPage } from './NotFoundPage';

type UrlParams = { brandSlug: string };

type BrandCmsSections = {
	brandHeader: JsxArgs | null;
	gridBannerPlacements: JsxArgs[];
	hasGridBannerPlacement: boolean;
};

function isCmsNode(value: unknown): value is JsxArgs {
	return Array.isArray(value) && typeof value[0] === 'string' && typeof value[1] === 'object' && value[1] !== null;
}

function collectBrandCmsSections(cmsData: JsxArgs | null | undefined): BrandCmsSections {
	if (!isCmsNode(cmsData)) {
		return { brandHeader: null, gridBannerPlacements: [], hasGridBannerPlacement: false };
	}

	let brandHeader: JsxArgs | null = null;
	const gridBannerPlacements: JsxArgs[] = [];
	let hasGridBannerPlacement = false;

	const visit = (node: JsxArgs) => {
		const [name, , ...children] = node;

		if (name === 'BrandHeader' && !brandHeader) {
			brandHeader = node;
		}

		if (name === 'GridBannerPlacement') {
			hasGridBannerPlacement = true;
			gridBannerPlacements.push(node);
		}

		for (const child of children) {
			if (isCmsNode(child)) {
				visit(child);
			}
		}
	};

	visit(cmsData);

	return { brandHeader, gridBannerPlacements, hasGridBannerPlacement };
}

export const BrandPage = (): JSX.Element => {
	const { brandSlug } = useParams<UrlParams>();
	const { data, error } = useApi(Res.brandMetaData, { brandSlug });
	const { sendEventOnce } = useAnalytics();
	const { pathname } = useLocation();
	const ui = UiContainer.useContainer();
	const { isFilterButtonsSticky } = ui;
	const { gridBannerBrandSlugs: gridBannerBrandPermas = [] } = useConfig();
	const config = useConfig();
	const { data: cmsIndex } = useCms(Res.cmsIndex, {});
	// a draft of a file never published is in nobody's index, so the preview link is what says to ask
	const previewing = previewedFile(useLocation().search);
	const isBrandCollaborator =
		previewing === Res.brandHeader.uri({ brand: brandSlug }) ||
		hasCmsFile(cmsIndex, 'brands', brandSlug, config.brandCollaborators);
	const { data: brandHeader } = useCms(isBrandCollaborator ? Res.brandHeader : null, { brand: brandSlug });

	const shouldFetchCms =
		previewing === Res.brandCmsContent.uri({ brandSlug }) ||
		hasCmsFile(cmsIndex, 'brands', `products-banners/${brandSlug}`, gridBannerBrandPermas);
	const { data: brandCmsData } = useCms(shouldFetchCms ? Res.brandCmsContent : null, { brandSlug });

	// Mobile row offset logic for banner positioning
	const getMobileRowWithOffset = (originalRow: number) => {
		let offset = 1; // Base offset for mobile (other elements render first)
		return originalRow + offset;
	};

	// Function to apply mobile offset to CMS data structure
	const applyMobileOffsetToCmsData = (cmsData: JsxArgs, offsetFunction: (row: number) => number): JsxArgs => {
		if (!Array.isArray(cmsData)) return cmsData;

		const [componentName, props, ...children] = cmsData;

		// If this is a GridBannerPlacement component, apply offset to mobile coordinates
		if (componentName === 'GridBannerPlacement' && props && typeof props === 'object') {
			const rawMobileStartRow = (props as any).mobileStartRow;
			const mobileStartRowNum =
				typeof rawMobileStartRow === 'string' ? Number(rawMobileStartRow) : (rawMobileStartRow as number);

			const updatedProps = {
				...props,
				// Shift the mobile start row by the calculated offset; height stays the same
				mobileStartRow: String(offsetFunction(mobileStartRowNum)),
			};
			return [
				componentName,
				updatedProps,
				...children.map((child) =>
					Array.isArray(child) ? applyMobileOffsetToCmsData(child, offsetFunction) : child
				),
			];
		}

		// Recursively process children for nested structures
		return [
			componentName,
			props,
			...children.map((child) =>
				Array.isArray(child) ? applyMobileOffsetToCmsData(child, offsetFunction) : child
			),
		];
	};

	// Apply mobile offset logic to CMS data if available
	const processedBrandHeader = brandHeader ? applyMobileOffsetToCmsData(brandHeader, getMobileRowWithOffset) : null;
	const processedBrandCmsData =
		shouldFetchCms && brandCmsData ? applyMobileOffsetToCmsData(brandCmsData, getMobileRowWithOffset) : null;

	const processedBrandHeaderSections = useMemo(
		() => collectBrandCmsSections(processedBrandHeader),
		[processedBrandHeader]
	);
	const processedBrandCmsSections = useMemo(
		() => collectBrandCmsSections(processedBrandCmsData),
		[processedBrandCmsData]
	);

	const displayedBrandHeader =
		processedBrandHeaderSections.brandHeader ??
		processedBrandCmsSections.brandHeader ??
		(!processedBrandHeaderSections.hasGridBannerPlacement ? processedBrandHeader : null);
	const gridBannerPlacements = [
		...processedBrandHeaderSections.gridBannerPlacements,
		...processedBrandCmsSections.gridBannerPlacements,
	];

	// Render CMS banners if available
	const extraMarkup = gridBannerPlacements.map((structure, index) => (
		<CmsComponent key={`cms-banner-${index}`} structure={structure} />
	));

	const t = { ...useTranslation().brand, ...useTranslation().category };
	const query = useQuery<{ page?: string }>();
	const { searchProfileId, randomizeSearchProfileId } = MetaContainer.useContainer();
	const { sort, price, brand, inStock, campaign, isEco } = useQuery<{
		sort: string;
		price: string;
		brand: string;
		inStock: string;
		campaign: string;
		isEco: string;
	}>();
	const page = +(query.page ?? '1') || 1;
	const absolutePath = `https://${config.domain}${pathname}`;
	const canonicalUrl = page && page > 1 ? `${absolutePath}?page=${page}` : absolutePath;
	const limit = LOOP54_DEFAULT_PAGE_SIZE;
	const offset = 0;
	const location = useLocation();
	const filterQueryparams = useFilterQueryParams(location.search);
	const { filters, setFilters, clearFilters } = FilterContainer.useContainer();
	const history = useHistory();
	const filtersRef = useRef(filters);
	const cleanupBrandRef = useRef<string>(brandSlug);
	const [filtersActive, setFiltersActive] = useState(true);

	useEffect(() => {
		filtersRef.current = filters;
	}, [filters]);
	const previousBrandSlugRef = useRef<string | undefined>(brandSlug);
	const hasLoadedCategoriesRef = useRef<boolean>(false);

	useEffect(() => {
		if (cleanupBrandRef.current === brandSlug) {
			return;
		}

		const isBackNavigation = history.action === 'POP';
		cleanupBrandRef.current = brandSlug;

		if (isBackNavigation) {
			return;
		}

		const previousFilters = filtersRef.current;
		if (previousFilters.length > 0) {
			const searchParams = new URLSearchParams(location.search);
			let updated = false;

			previousFilters.forEach((filter) => {
				if (searchParams.has(filter.name)) {
					searchParams.delete(filter.name);
					updated = true;
				}
			});

			if (updated) {
				const newSearch = searchParams.toString();
				history.replace({
					pathname: location.pathname,
					search: newSearch ? `?${newSearch}` : '',
				});
			}
		}

		setFiltersActive(false);
		clearFilters();
	}, [brandSlug, history, location.pathname, location.search, clearFilters]);

	const loop54PostHeaders = {
		'Api-Version': 'V3',
		'User-id': randomizeSearchProfileId ? generateUUID() : searchProfileId,
	};
	const loop54BrandProductsPayload = useMemo(() => {
		const shouldApplyFilters = filtersActive && cleanupBrandRef.current === brandSlug;
		const filtersToApply = shouldApplyFilters ? filters : [];

		return buildLoopBrandsPayload(
			brandSlug,
			sort,
			limit,
			offset,
			filtersToApply,
			filterQueryparams,
			price,
			brand,
			inStock,
			campaign,
			isEco
		);
	}, [
		brandSlug,
		sort,
		limit,
		offset,
		filters,
		filtersActive,
		filterQueryparams,
		price,
		brand,
		inStock,
		campaign,
		isEco,
	]);

	const { loop54Api } = useConfig();
	const { data: brandsProductsData } = useApi(
		Res.brandProductsLoop54,
		{
			brandSlug,
			limit,
			offset,
			sort: sort ?? '',
			price: price ?? '',
			brand: brand ?? '',
			inStock: inStock ?? '',
			campaign: campaign ?? '',
			isEco: isEco ?? '',
			...filterQueryparams,
		},
		loop54Api,
		loop54BrandProductsPayload,
		loop54PostHeaders,
		'POST'
	);

	useEffect(() => {
		if (brandsProductsData) {
			setFilters(brandsProductsData.results.facets);
			setFiltersActive(true);
			// Mark that we've loaded categories for this brand
			hasLoadedCategoriesRef.current = true;
		}
	}, [brandsProductsData]);

	// Track brand changes to reset the loaded categories flag
	useEffect(() => {
		if (previousBrandSlugRef.current !== brandSlug) {
			// Brand changed, reset the loaded categories flag
			hasLoadedCategoriesRef.current = false;
			previousBrandSlugRef.current = brandSlug;
		}
	}, [brandSlug]);

	function handleSendEvent(products: ApiProduct[]) {
		if (!data) return;
		sendEventOnce(
			{
				event: 'view_item_list',
				ecommerce: {
					item_list_id: String(data.id),
					item_list_name: data.name,

					items: products.map((p) => ({
						item_id: String(p.id),
						item_name: p.name,
						price: p.price,
					})),
				},
			},
			brandSlug
		);
	}
	function highlightId(isFirstPage: boolean, data: ApiCategoryProductsLoop54): number | null {
		// only highlight prooduct on first page
		if (!isFirstPage) return null;
		// only highlight product for "Popular" sorting
		if (sort) return null;
		// only highlight when no filters are applied
		if (data.results.facets.some((f) => f.type === 'distinct' && f.items?.some((i) => i.selected))) return null;
		return data.results.items[0]?.id;
	}

	// only a real not-found earns the not-found page; a hiccup upstream keeps the loader
	if (isNotFound(error)) return <NotFoundPage />;
	if (error || !data) return <LoaderPage />;

	const productsPreamble = (
		<>
			<div className="mobile-buttons">
				<div className={`filter-wrapper ${isFilterButtonsSticky ? 'sticky' : ''}`}>
					<FilterButton />
					<SortingChooser perma={brandSlug} />
				</div>
			</div>
		</>
	);

	return (
		<Main>
			<div className="brand-page">
				<Helmet>
					<title>
						{renderTemplate(t.title, { brandName: data.name })}
						{page > 1 ? ' - ' + t.page + ' ' + page : ''}
					</title>
					<meta name="description" content={renderTemplate(t.metaDesc, { brandName: data.name })} />
					<meta name="keywords" content={data.name} />
					<link rel="canonical" href={canonicalUrl} />
				</Helmet>
				<BrandBreadcrumbs slug={data.slug} name={data.name} />
				{displayedBrandHeader && <CmsComponent structure={displayedBrandHeader} />}
				{!displayedBrandHeader && (
					<div className="heading-filter-grid">
						<h1 className="category-heading">
							{data.name}
							{page > 1 ? ' - ' + t.page + ' ' + page : ''}
						</h1>
						<RawHtml html={data.description} />
					</div>
				)}
				{(() => {
					const bottomCategoryFilter = filters.find((filter) => filter.name === 'bottomCategory');
					// Only show loading skeleton if we haven't loaded categories for this brand yet
					// This prevents skeleton from showing during filter changes on the same brand page
					const isLoading = !hasLoadedCategoriesRef.current;

					// Show loading skeleton only when we haven't loaded categories for this brand yet
					if (isLoading) {
						return <CmsPopularCategories categories={[]} isLoading={true} />;
					}

					if (
						bottomCategoryFilter &&
						bottomCategoryFilter.type === 'distinct' &&
						bottomCategoryFilter.items
					) {
						// Sort items by count in descending order and take top 5
						const sortedItems = [...bottomCategoryFilter.items].sort((a, b) => b.count - a.count);
						const topItems = sortedItems.slice(0, 5);

						// Transform FacetItem[] to PopularCategoryItem[]
						const transformedCategories = topItems.map((item) => ({
							title: item.item,
							img: '/images/arrow-right.svg',
							link: `${pathname}?bottomCategory=${encodeURIComponent(item.item)}`,
							isSelected: item.selected,
						}));

						const handleCategoryClick = (categoryItem: PopularCategoryItem) => {
							// Find the corresponding filter item to check if it's already selected
							const filterItem = bottomCategoryFilter.items?.find(
								(item) => item.item === categoryItem.title
							);
							const isCurrentlySelected = filterItem?.selected || false;

							// Enforce single selection for bottomCategory:
							// - If the clicked item is already selected, clear all selections for this facet.
							// - Otherwise, select only the clicked item and deselect all others.
							const updatedFilters = filters.map((filter) => {
								if (filter.name !== 'bottomCategory' || filter.type !== 'distinct' || !filter.items) {
									return filter;
								}

								return {
									...filter,
									items: filter.items.map((item) => ({
										...item,
										selected: isCurrentlySelected ? false : item.item === categoryItem.title,
									})),
								};
							});

							setFilters(updatedFilters);
						};

						return transformedCategories.length > 1 ? (
							<CmsPopularCategories
								categories={transformedCategories}
								onClick={handleCategoryClick}
								isLoading={false}
								prefixItem={true}
							/>
						) : null;
					}
					return null;
				})()}
				<div className="filter-wrapper desktop">
					<FilterBadges hits={brandsProductsData?.results.count} perma={brandSlug} />
					<FilterButton />
					<SortingChooser perma={brandSlug} />
				</div>
				<FilterPanel
					perma={brandSlug}
					facets={brandsProductsData?.results.facets}
					limitedCustomFilters={['bottomCategory']}
					filterPage="brands"
					filterEventData={{ brandId: data.slug }}
				/>
				<PagesLoop54
					resource={Res.brandProductsLoop54}
					params={{
						brandSlug,
						sort: sort ?? '',
						price: price ?? '',
						brand: brand ?? '',
						inStock: inStock ?? '',
						campaign: campaign ?? '',
						isEco: isEco ?? '',
						...filterQueryparams,
					}}
					onRender={(data) => handleSendEvent(data.results.items)}
					preamble={productsPreamble}
					buildChildren={(data, isFirstPage) => {
						return (
							<>
								{data.results.items.map((p) => (
									<ProductBox
										key={p.id}
										product={p}
										highlight={p.id === highlightId(isFirstPage, data)}
									/>
								))}
							</>
						);
					}}
					postPayload={loop54BrandProductsPayload}
					postHeaders={loop54PostHeaders}
					extraMarkup={extraMarkup}
				/>
			</div>
		</Main>
	);
};

export const brandRouting: Routing<UrlParams> = {
	path(urls) {
		return `${urls.brands}/:brandSlug`;
	},
	component: BrandPage,
	requiresAuth: false,
	preload: async (config, routeMatch: match<UrlParams>, query: string, _categories?, cmsIndex?: CmsIndex | null) => {
		const { brandSlug } = routeMatch.params;
		const page = parsePagesQueryLoop54(query);
		const { sort, limit, offset, price, brand, inStock, campaign, isEco, bottomCategory } = page;
		const filterQueryparams = useFilterQueryParams(query);
		const previewing = previewedFile(query);
		const isBrandCollaborator =
			previewing === Res.brandHeader.uri({ brand: brandSlug }) ||
			hasCmsFile(cmsIndex, 'brands', brandSlug, config.brandCollaborators);
		const shouldFetchCms =
			previewing === Res.brandCmsContent.uri({ brandSlug }) ||
			hasCmsFile(cmsIndex, 'brands', `products-banners/${brandSlug}`, config.gridBannerBrandSlugs);

		const { loop54Api } = config;
		const loop54PostHeaders = {
			'Api-Version': 'V3',
			'User-id': generateUUID(), // use randomized id in ssr
		};

		let loop54CategoryProductsPayload: Loop54CategoryProductsRequestBody = buildLoopBrandsPayload(
			brandSlug,
			sort,
			limit,
			offset,
			[], // only used in client side
			filterQueryparams,
			price,
			brand,
			inStock,
			campaign,
			isEco,
			bottomCategory
		);

		const assets: Record<string, unknown>[] = await Promise.all([
			fetchResourceMap(
				config,
				Res.brandProductsLoop54,
				{ brandSlug, ...page, ...filterQueryparams },
				loop54Api,
				loop54CategoryProductsPayload,
				loop54PostHeaders,
				'POST'
			),
			fetchResourceMap(config, Res.brandMetaData, { brandSlug }),
			...(isBrandCollaborator
				? [optionalResource(fetchResourceMap(config, Res.brandHeader, { brand: brandSlug }, config.cmsContent))]
				: []),
			...(shouldFetchCms
				? [optionalResource(fetchResourceMap(config, Res.brandCmsContent, { brandSlug }, config.cmsContent))]
				: []),
		]);
		return Object.assign({}, ...assets);
	},
};

const buildLoopBrandsPayload = (
	brandSlug: string,
	sort: string | undefined,
	limit: number,
	offset: number,
	filters: ApiFacetLoop54[],
	filterParams: Record<string, string>, // dynamic filter query params
	price?: string,
	_brand?: string,
	inStock?: string,
	campaign?: string,
	isEco?: string,
	bottomCategory?: string
): Loop54CategoryProductsRequestBody => {
	let facets: FacetPayloadLoop54[] = [
		{
			name: 'price',
			attributeName: 'price',
			type: 'range',
		},
		{
			name: 'inStock',
			attributeName: 'inStock',
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		},
		{
			name: 'campaign',
			attributeName: 'campaign',
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		},
		{
			name: 'isEco',
			attributeName: 'isEco',
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		},
		{
			name: 'bottomCategory',
			attributeName: 'bottomCategory',
			type: 'distinct',
			sortBy: [
				{
					type: 'item',
					order: 'asc',
				},
			],
		},
	];

	// dead code. left as reference
	// custom facets that came from loop api response
	if (filters && undefined) {
		const unmatchedFacets = filters.filter((filter) => !facets.some((facet) => facet.name === filter.name));
		unmatchedFacets.forEach((filter) => {
			facets.push({
				name: filter.name,
				type: filter.type,
				attributeName: filter.name,
				// we'll populate `selected` below through filterParams
				/* selected: [], */
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			});
		});
	}

	// Process client-side filters from FilterContainer
	if (filters && filters.length > 0) {
		filters.forEach((filter) => {
			if (filter.type === 'distinct' && filter.items) {
				const appliedItems = filter.items.filter((item) => item.selected).map((item) => item.item);
				if (appliedItems.length > 0) {
					const correspondingFacet = facets.find((facet) => facet.name === filter.name);
					if (correspondingFacet) {
						correspondingFacet.selected = appliedItems;
					} else {
						// Add new distinct facet
						const newFacet: FacetPayloadLoop54 = {
							name: filter.name,
							attributeName: filter.name,
							type: 'distinct',
							selected: appliedItems,
							sortBy: [
								{
									type: 'item',
									order: 'asc',
								},
							],
						};
						facets.push(newFacet);
					}
				}
			} else if (filter.type === 'range') {
				const rangeSelection = resolveRangeSelectionForFacet(filter);
				if (rangeSelection) {
					const correspondingFacet = facets.find((facet) => facet.name === filter.name);
					if (correspondingFacet) {
						correspondingFacet.selected = { min: rangeSelection.min, max: rangeSelection.max };
					} else {
						// Add new range facet
						const newFacet: FacetPayloadLoop54 = {
							name: filter.name,
							attributeName: filter.name,
							type: 'range',
							selected: { min: rangeSelection.min, max: rangeSelection.max },
						};
						facets.push(newFacet);
					}
				}
			}
		});
	}

	// filter params for ssr of dynamic filters (only process if no client-side filters)
	const hasClientSideFilters =
		filters &&
		filters.some((filter) => {
			if (filter.type === 'distinct' && filter.items) {
				return filter.items.some((item) => item.selected);
			}
			if (filter.type === 'range') {
				return isRangeFacetAppliedStatic(filter);
			}
			return false;
		});
	if (!hasClientSideFilters) {
		Object.entries(filterParams).forEach(([key, value]) => {
			const correspondingFacet = facets.find((facet) => facet.name === key);

			if (correspondingFacet) {
				const selectedValues = multivalueFiltersToStrArray(value);
				if (selectedValues.length > 0) {
					correspondingFacet.selected = selectedValues;
				}
			} else {
				// dynamic facets
				const facetType = value.includes(FILTER_RANGE_VALUE_DELIMITER) ? 'range' : 'distinct';
				const newFacet: FacetPayloadLoop54 = {
					name: key,
					attributeName: key,
					type: facetType,
					selected:
						facetType === 'distinct' ? multivalueFiltersToStrArray(value) : rangeValueToFacetObj(value),
					...(facetType === 'distinct' && {
						sortBy: [
							{
								type: 'item',
								order: 'asc',
							},
						],
					}),
				};
				facets.push(newFacet);
			}
		});
	}

	if (price) {
		const facetRangeObj = rangeValueToFacetObj(price);
		const correspondingFacet = facets.find((facet) => facet.name === 'price');

		if (correspondingFacet) {
			correspondingFacet.selected = facetRangeObj;
		} else {
			const priceFacet: FacetPayloadLoop54 = {
				name: 'price',
				attributeName: 'price',
				type: 'range',
				selected: facetRangeObj,
			};
			facets.push(priceFacet);
		}
	}

	if (inStock) {
		const correspondingFacet = facets.find((facet) => facet.name === 'inStock');
		if (correspondingFacet) {
			correspondingFacet.selected = ['true'];
		} else {
			const inStockFacet: FacetPayloadLoop54 = {
				name: 'inStock',
				attributeName: 'inStock',
				type: 'distinct',
				selected: ['true'],
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			};
			facets.push(inStockFacet);
		}
	}

	if (campaign) {
		const correspondingFacet = facets.find((facet) => facet.name === 'campaign');
		if (correspondingFacet) {
			correspondingFacet.selected = ['true'];
		} else {
			const campaignFacet: FacetPayloadLoop54 = {
				name: 'campaign',
				attributeName: 'campaign',
				type: 'distinct',
				selected: ['true'],
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			};
			facets.push(campaignFacet);
		}
	}

	if (isEco) {
		const correspondingFacet = facets.find((facet) => facet.name === 'isEco');
		if (correspondingFacet) {
			correspondingFacet.selected = [true];
		} else {
			const isEcoFacet: FacetPayloadLoop54 = {
				name: 'isEco',
				attributeName: 'isEco',
				type: 'distinct',
				selected: ['true'],
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			};
			facets.push(isEcoFacet);
		}
	}

	if (bottomCategory) {
		const correspondingFacet = facets.find((facet) => facet.name === 'bottomCategory');
		if (correspondingFacet) {
			correspondingFacet.selected = multivalueFiltersToStrArray(bottomCategory);
		} else {
			const isBottomCategoryFacet: FacetPayloadLoop54 = {
				name: 'bottomCategory',
				attributeName: 'bottomCategory',
				type: 'distinct',
				selected: multivalueFiltersToStrArray(bottomCategory),
				sortBy: [
					{
						type: 'item',
						order: 'asc',
					},
				],
			};
			facets.push(isBottomCategoryFacet);
		}
	}

	let loop54BrandProductsPayload: Loop54CategoryProductsRequestBody = {
		attribute: {
			name: 'brandSlug',
			value: brandSlug,
		},
		resultsOptions: {
			facets,
			take: limit,
			skip: offset,
		},
	};
	loop54BrandProductsPayload.resultsOptions.sortBy = determineSortPayload(sort);

	return loop54BrandProductsPayload;
};
